import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const animatedBackgroundVariants = cva(
  'absolute inset-0 pointer-events-none overflow-hidden',
  {
    variants: {
      variant: {
        hero: '',
        light: '',
        cta: '',
        aurora: '',
      },
      intensity: {
        subtle: '',
        normal: '',
        vibrant: '',
      },
    },
    defaultVariants: {
      variant: 'hero',
      intensity: 'normal',
    },
  }
);

export interface AnimatedBackgroundProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof animatedBackgroundVariants> {}

// ─── Orb Sub-component ────────────────────────────────────────────────
interface OrbProps {
  gradient: string;
  size: string;
  position: string;
  animation: string;
  blur?: string;
  delay?: string;
}

const Orb: React.FC<OrbProps> = ({
  gradient,
  size,
  position,
  animation,
  blur = 'blur-3xl',
  delay,
}) => (
  <div
    className={cn(
      'animated-bg-orb absolute rounded-full bg-radial will-change-transform',
      gradient,
      size,
      position,
      animation,
      blur
    )}
    style={delay ? { animationDelay: delay } : undefined}
    aria-hidden="true"
  />
);

// ─── Floating Particles ───────────────────────────────────────────────
interface ParticleProps {
  color: string;
  size: string;
  position: string;
  animation: string;
  delay?: string;
}

const Particle: React.FC<ParticleProps> = ({ color, size, position, animation, delay }) => (
  <div
    className={cn(
      'animated-bg-particle absolute rounded-full will-change-transform',
      color, size, position, animation
    )}
    style={delay ? { animationDelay: delay } : undefined}
    aria-hidden="true"
  />
);

// ─── Hero Variant ─────────────────────────────────────────────────────
const HeroOrbs: React.FC<{ intensity: string }> = ({ intensity }) => {
  const colors = {
    subtle: {
      cyan: 'from-cyan-500/20 to-transparent',
      purple: 'from-purple-500/15 to-transparent',
      blue: 'from-blue-500/15 to-transparent',
    },
    normal: {
      cyan: 'from-cyan-500/40 to-transparent',
      purple: 'from-purple-500/30 to-transparent',
      blue: 'from-blue-500/25 to-transparent',
    },
    vibrant: {
      cyan: 'from-cyan-500/50 to-transparent',
      purple: 'from-purple-500/40 to-transparent',
      blue: 'from-blue-500/35 to-transparent',
    },
  }[intensity] || {
    cyan: 'from-cyan-500/40 to-transparent',
    purple: 'from-purple-500/30 to-transparent',
    blue: 'from-blue-500/25 to-transparent',
  };

  return (
    <>
      <Orb
        gradient={colors.cyan}
        size="w-96 h-96 sm:w-[28rem] sm:h-[28rem]"
        position="top-0 right-0 translate-x-1/4 -translate-y-1/4"
        animation="animate-float-orb-1"
      />
      <Orb
        gradient={colors.purple}
        size="w-80 h-80 sm:w-96 sm:h-96"
        position="top-1/3 -left-20"
        animation="animate-float-orb-2"
        delay="-3s"
      />
      <Orb
        gradient={colors.blue}
        size="w-64 h-64 sm:w-80 sm:h-80"
        position="bottom-0 left-1/2 -translate-x-1/2 translate-y-1/4"
        animation="animate-float-orb-3"
        delay="-7s"
      />
      {/* Aurora mesh */}
      <div
        className="animated-bg-aurora absolute inset-0 opacity-40 bg-gradient-to-r from-cyan-500/15 via-purple-500/10 to-blue-500/15 bg-[length:200%_200%] animate-aurora"
        aria-hidden="true"
      />
      {/* Floating particles */}
      <Particle
        color="bg-cyan-400/80"
        size="w-1.5 h-1.5"
        position="bottom-1/4 left-1/4"
        animation="animate-particle-1"
      />
      <Particle
        color="bg-purple-400/60"
        size="w-1 h-1"
        position="bottom-1/3 right-1/3"
        animation="animate-particle-2"
        delay="-3s"
      />
      <Particle
        color="bg-blue-400/70"
        size="w-1.5 h-1.5"
        position="bottom-1/2 left-1/2"
        animation="animate-particle-3"
        delay="-6s"
      />
      <Particle
        color="bg-cyan-300/50"
        size="w-1 h-1"
        position="bottom-2/3 right-1/4"
        animation="animate-particle-1"
        delay="-2s"
      />
      <Particle
        color="bg-purple-300/60"
        size="w-2 h-2"
        position="bottom-1/4 right-1/2"
        animation="animate-particle-2"
        delay="-5s"
      />
      <Particle
        color="bg-blue-300/40"
        size="w-1 h-1"
        position="bottom-1/3 left-1/3"
        animation="animate-particle-3"
        delay="-1s"
      />
    </>
  );
};

// ─── CTA Variant ──────────────────────────────────────────────────────
const CtaOrbs: React.FC<{ intensity: string }> = ({ intensity }) => {
  const colors = {
    subtle: {
      emerald: 'from-emerald-500/20 to-transparent',
      cyan: 'from-cyan-500/15 to-transparent',
    },
    normal: {
      emerald: 'from-emerald-500/40 to-transparent',
      cyan: 'from-cyan-500/30 to-transparent',
    },
    vibrant: {
      emerald: 'from-emerald-500/50 to-transparent',
      cyan: 'from-cyan-500/40 to-transparent',
    },
  }[intensity] || {
    emerald: 'from-emerald-500/40 to-transparent',
    cyan: 'from-cyan-500/30 to-transparent',
  };

  return (
    <>
      <Orb
        gradient={colors.emerald}
        size="w-80 h-80 sm:w-96 sm:h-96"
        position="-left-20 top-1/2 -translate-y-1/2"
        animation="animate-float-orb-2"
      />
      <Orb
        gradient={colors.cyan}
        size="w-72 h-72 sm:w-80 sm:h-80"
        position="-right-16 top-1/4"
        animation="animate-float-orb-1"
        delay="-4s"
      />
      {/* Particles */}
      <Particle
        color="bg-emerald-400/70"
        size="w-1.5 h-1.5"
        position="bottom-1/3 left-1/3"
        animation="animate-particle-1"
      />
      <Particle
        color="bg-cyan-400/60"
        size="w-1 h-1"
        position="bottom-1/4 right-1/3"
        animation="animate-particle-2"
        delay="-4s"
      />
      <Particle
        color="bg-emerald-300/50"
        size="w-1 h-1"
        position="bottom-1/2 left-1/2"
        animation="animate-particle-3"
        delay="-2s"
      />
    </>
  );
};

// ─── Light Variant ────────────────────────────────────────────────────
const LightBackground: React.FC<{ intensity: string }> = ({ intensity }) => {
  const gridOpacity =
    intensity === 'subtle'
      ? 'opacity-[0.02]'
      : intensity === 'vibrant'
        ? 'opacity-[0.06]'
        : 'opacity-[0.04]';
  const glowOpacity =
    intensity === 'subtle'
      ? 'opacity-10'
      : intensity === 'vibrant'
        ? 'opacity-25'
        : 'opacity-15';

  return (
    <>
      <div
        className={cn('animated-bg-dots absolute inset-0 animate-grid-fade', gridOpacity)}
        style={{
          backgroundImage: 'radial-gradient(circle, currentColor 1px, transparent 1px)',
          backgroundSize: '24px 24px',
          color: '#475569',
        }}
        aria-hidden="true"
      />
      <div
        className={cn(
          'animated-bg-orb absolute -top-20 -right-20 w-80 h-80 rounded-full animate-pulse-glow blur-3xl will-change-transform',
          'bg-gradient-to-br from-blue-200/40 to-indigo-200/20',
          glowOpacity
        )}
        aria-hidden="true"
      />
      <div
        className={cn(
          'animated-bg-orb absolute -bottom-20 -left-20 w-64 h-64 rounded-full animate-pulse-glow blur-3xl will-change-transform',
          'bg-gradient-to-br from-cyan-200/30 to-emerald-200/15',
          glowOpacity
        )}
        style={{ animationDelay: '-3s' }}
        aria-hidden="true"
      />
    </>
  );
};

// ─── Aurora Variant ───────────────────────────────────────────────────
const AuroraBackground: React.FC<{ intensity: string }> = ({ intensity }) => {
  const opacity =
    intensity === 'subtle'
      ? 'opacity-20'
      : intensity === 'vibrant'
        ? 'opacity-50'
        : 'opacity-30';

  return (
    <div
      className={cn(
        'animated-bg-aurora absolute inset-0 bg-gradient-to-r from-cyan-500/10 via-purple-500/10 to-emerald-500/10 bg-[length:200%_200%] animate-aurora',
        opacity
      )}
      aria-hidden="true"
    />
  );
};

// ─── Main Component ───────────────────────────────────────────────────
const AnimatedBackground = React.forwardRef<HTMLDivElement, AnimatedBackgroundProps>(
  ({ className, variant = 'hero', intensity = 'normal', ...props }, ref) => {
    const intensityStr = intensity || 'normal';

    return (
      <div
        ref={ref}
        className={cn(animatedBackgroundVariants({ variant, intensity, className }))}
        {...props}
      >
        {variant === 'hero' && <HeroOrbs intensity={intensityStr} />}
        {variant === 'cta' && <CtaOrbs intensity={intensityStr} />}
        {variant === 'light' && <LightBackground intensity={intensityStr} />}
        {variant === 'aurora' && <AuroraBackground intensity={intensityStr} />}
      </div>
    );
  }
);
AnimatedBackground.displayName = 'AnimatedBackground';

export { AnimatedBackground, animatedBackgroundVariants };
